import { isValidElement, useEffect, useRef, useState } from "react";
import style from "./AbsoluteBlock.module.scss";
import cn from "classnames";
import { createPortal } from "react-dom";

export function AbsoluteBlock({
    targetElement,
    content,
    XAlign = "center",
    advancedAdaptive = false
}: {
    targetElement: JSX.Element,
    content: JSX.Element | ((isOpen: boolean) => JSX.Element),
    XAlign?: "left" | "center" | "right",
    advancedAdaptive?: boolean
}) {
    const refContainer = useRef<HTMLDivElement | null>(null);
    const refTarget = useRef<HTMLDivElement | null>(null);
    const refContent = useRef<HTMLDivElement | null>(null);

    const [smallScreen, setSmallScreen] = useState(() => window.innerWidth < 1024);
    const mobileDialog = advancedAdaptive && smallScreen;

    useEffect(() => {
        const resize = () => setSmallScreen(window.innerWidth < 1024);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [position, setPosition] = useState<{ top: number, left: number, transform: string }>({ top: 0, left: 0, transform: "" });

    useEffect(() => {
        function handleClick(e: any) {
            if (!refContainer.current?.contains(e.target) && !refContent.current?.contains(e.target)) {
                setIsOpen(false);
            }
        }

        document.documentElement.addEventListener("click", handleClick);

        return () => {
            document.documentElement.removeEventListener("click", handleClick);
        };
    }, []);

    useEffect(() => {
        if (!isOpen || mobileDialog) return;
        function handleScroll(event: Event) {
            if (event.target instanceof Node && refContent.current?.contains(event.target)) return;
            setIsOpen(false);
        }
        const scrollableItems = document.querySelectorAll("[data-scrollable]");
        scrollableItems.forEach(item => item.addEventListener("scroll", handleScroll));
        window.addEventListener("scroll", handleScroll);
        return () => {
            scrollableItems.forEach(item => item.removeEventListener("scroll", handleScroll));
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isOpen, mobileDialog]);

    useEffect(() => {
        if (!isOpen || !mobileDialog) return;
        const previousOverflow = document.body.style.overflow;
        const trigger = refTarget.current?.querySelector<HTMLElement>("button, [tabindex]");
        document.body.style.overflow = "hidden";
        refContent.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            trigger?.focus();
        };
    }, [isOpen, mobileDialog]);

    function preCalcPosition(callback: () => void) {
        const target = refTarget.current;
        const content = refContent.current;

        if (target && content) {
            let percentage = "50%";

            if (XAlign === "left") {
                percentage = "90%";
            } else if (XAlign === "right") {
                percentage = "10%";
            }

            const targetRect = target.getBoundingClientRect();
            const contentHeight = content.offsetHeight;
            const contentWidth = content.offsetWidth;

            const isIntersectionY = targetRect.bottom + contentHeight > window.innerHeight;
            let isIntersectionXLeft = false;
            let isIntersectionXRight = false;

            if (targetRect.left >= (window.innerWidth - targetRect.right)) {
                isIntersectionXRight = (window.innerWidth - targetRect.right) < calculatePercentage(contentWidth, percentage);
            } else {
                isIntersectionXLeft = targetRect.left < calculatePercentage(contentWidth, percentage);
            }

            // Derive the scale from the actual trigger: admin mobile/tablet
            // now renders at 100%, while desktop still uses the existing zoom.
            const zoomFactor = targetRect.width ? target.offsetWidth / targetRect.width : 1;

            setPosition({
                left: (targetRect.left + targetRect.width / 2) * zoomFactor,
                top: isIntersectionY
                    ? (targetRect.top - contentHeight - 15) * zoomFactor +  (zoomFactor > 1.01 ? 90 : 0)
                    : (targetRect.bottom + 15) * zoomFactor,
                transform:
                    isIntersectionXLeft ? `translateX(-${(targetRect.width / 2) * zoomFactor}px)`
                        : isIntersectionXRight ? `translateX(calc((-1 * 100%) + ${(targetRect.width / 2) * zoomFactor}px))`
                            : `translateX(-${percentage})`
            });
        }

        callback();
    }

    const panel = (
        <div
            style={mobileDialog ? undefined : position}
            className={cn(style.backing, isOpen && style.backing_open,
                mobileDialog && style.mobileDialog)}
            onClick={event => { if (event.target === event.currentTarget) setIsOpen(false); }}>
            <div ref={refContent} className={style.absoluteBlock}
                role={mobileDialog ? "dialog" : undefined}
                aria-modal={mobileDialog || undefined}
                aria-label={mobileDialog ? "Редагування" : undefined}
                tabIndex={mobileDialog ? -1 : undefined}
                onKeyDown={event => {
                    if (event.key === "Escape") { event.stopPropagation(); setIsOpen(false); }
                    if (!mobileDialog || event.key !== "Tab") return;
                    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
                        'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'
                    )).filter(item => item.tabIndex >= 0 && item.getClientRects().length > 0);
                    const first = controls[0], last = controls[controls.length - 1];
                    if (!first) { event.preventDefault(); return; }
                    if (document.activeElement === event.currentTarget) {
                        event.preventDefault(); (event.shiftKey ? last : first).focus();
                    } else if (event.shiftKey && document.activeElement === first) {
                        event.preventDefault(); last.focus();
                    } else if (!event.shiftKey && document.activeElement === last) {
                        event.preventDefault(); first.focus();
                    }
                }}>
                {mobileDialog && <button type="button" className={style.closeButton}
                    aria-label="Закрити вікно" onClick={() => setIsOpen(false)}>Закрити ×</button>}
                {isJSXElement(content) ? content : content(isOpen)}
            </div>
        </div>
    );

    return <div ref={refContainer} className={style.container}>
        <div ref={refTarget} onClick={() => {
            if (mobileDialog) setIsOpen(state => !state);
            else preCalcPosition(() => setIsOpen(state => !state));
        }}>{targetElement}</div>
        {mobileDialog ? (isOpen ? createPortal(panel, document.body) : null) : panel}
    </div>;
}

const isJSXElement = (value: unknown): value is JSX.Element => {
    return isValidElement(value);
};


function calculatePercentage(number: number, percentageString: string) {
    const percentage = parseFloat(percentageString.slice(0, -1));

    return (number * percentage) / 100;
}
